import { Box, Modal, Stack } from '@mui/material';
import { LinkReturnToMain } from 'Components';
import { SearchFieldTrigger } from 'Components/SearchFieldTrigger';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { RefObject } from 'react';
import { sendEvent } from 'Utils/sendEvent';
import { SearchInput } from '../SearchInput';
import { SearchContent } from '../SearchContent';
import { IRelatedAction, IResult } from '../hooks/useSearchLogic';

interface SearchModalProps {
  isActive: boolean;
  value: string;
  results: IResult[];
  relatedActions: IRelatedAction[];
  isLoading: boolean;
  showNoResults: boolean;
  showResults: boolean;
  minSearchLength: number;
  placeholder?: string;
  source?: string;
  searchBarRef: RefObject<HTMLButtonElement | null>;
  inputRef: RefObject<HTMLInputElement | null>;
  onActivate: () => void;
  onClose: () => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onClear: () => void;
  onActivityClick: (action: IRelatedAction) => void;
}

export const SearchModal = ({
  isActive,
  value,
  results,
  relatedActions,
  isLoading,
  showNoResults,
  showResults,
  minSearchLength,
  placeholder,
  source,
  searchBarRef,
  inputRef,
  onActivate,
  onClose,
  onInputChange,
  onKeyDown,
  onClear,
  onActivityClick,
}: SearchModalProps) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.ActivitiesLobby);
  const isDesktop = useIsDesktop();

  return (
    <>
      {!isActive && (
        <SearchFieldTrigger
          triggerRef={searchBarRef}
          label={placeholder || dictionary.searchButton}
          onActivate={() => {
            if (source === 'home') {
              sendEvent('Search Page', 'HomePageSearch');
            } else if (source === 'activities') {
              sendEvent('Activities Page', 'ActionsLobbySearch');
            }
            onActivate();
          }}
          height='56px'
          borderRadius='30px'
          sx={{
            width: isDesktop ? '564px' : '100%',
            mt: '24px',
            '&:focus': { outlineColor: 'rgb(21, 82, 232)' },
          }}
        />
      )}

      <Modal
        open={isActive}
        sx={{
          position: isDesktop ? 'absolute' : 'fixed',
          bgcolor: isDesktop ? undefined : 'primary.shade_50',
          right: 0,
          top: isDesktop ? 0 : '72px',
          height: isDesktop ? undefined : 'calc(100vh - 72px)',
          opacity: isActive ? 1 : 0,
          zIndex: isActive ? 99999 : -99999,
          overflow: isDesktop ? undefined : 'hidden',
          direction: 'rtl',
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        slotProps={{ backdrop: { onClick: onClose } }}
      >
        <Stack
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: '100vw',
            p: isDesktop ? '32px 64px 48px' : '20px 24px 88px',
            bgcolor: 'primary.shade_50',
            zIndex: isActive ? 99999 : -99999,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            overflow: isDesktop ? 'hidden' : 'auto',
            opacity: isActive ? 1 : 0,
            transition: isDesktop ? 'opacity .5s' : undefined,
            maxHeight: isDesktop ? undefined : 'calc(100vh - 72px)',
          }}
        >
          <LinkReturnToMain
            label={dictionary.back}
            onClick={onClose}
            tabIndex={0}
          />
          <Box mt='24px'>
            <SearchInput
              value={value}
              inputRef={inputRef}
              useCurtain={false}
              onInputChange={onInputChange}
              onKeyDown={onKeyDown}
              onClear={onClear}
            />
          </Box>
          <SearchContent
            value={value}
            results={results}
            relatedActions={relatedActions}
            isLoading={isLoading}
            showNoResults={showNoResults}
            showResults={showResults}
            minSearchLength={minSearchLength}
            onActivityClick={onActivityClick}
          />
        </Stack>
      </Modal>
      <Box
        sx={{
          position: 'fixed',
          width: '100vw',
          height: '100vh',
          top: '80px',
          right: 0,
          bgcolor: 'secondary.dark',
          opacity: isActive ? 0.8 : 0,
          zIndex: isActive ? 9999 : -9999,
          transition: isDesktop
            ? isActive
              ? 'opacity .5s'
              : undefined
            : undefined,
        }}
        onClick={onClose}
      />
    </>
  );
};
